import { useCallback } from 'react';

import { ApiCharts } from 'api';
import { useTypedSelector } from 'store';
import { web3Configs } from 'web3';

interface ILoadArgs {
  timeframe: string;
  startTime: number;
  endTime: number;
  limit: number;
  isConverted: boolean;
}

const useLoadDexChart = () => {
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair);

  const load = useCallback(
    async ({ timeframe, startTime, endTime, limit, isConverted }: ILoadArgs) => {
      let isError = false;
      let candles: [number, number, number, number, number, number][] = [];

      try {
        if (!dexPair) {
          return {
            candles,
            isError,
          };
        }

        const network = web3Configs[dexPair.network].geckoterminalSlug;
        const currency = isConverted ? 'usd' : 'token';
        const pool_address = dexPair.address ?? dexPair.pool_id ?? dexPair.extra?.pool_manager;
        let innerTimeframe: 'day' | 'minute' | 'hour' | undefined = undefined;
        let timeframeSec: number | undefined = 60;
        let aggregate: number | undefined = undefined;

        switch (timeframe) {
          case '1m': {
            innerTimeframe = 'minute';
            aggregate = 1;
            timeframeSec = 60;
            break;
          }
          case '5m': {
            innerTimeframe = 'minute';
            aggregate = 5;
            timeframeSec = 300;
            break;
          }
          case '15m': {
            innerTimeframe = 'minute';
            aggregate = 15;
            timeframeSec = 900;
            break;
          }
          case '1h': {
            innerTimeframe = 'hour';
            aggregate = 1;
            timeframeSec = 3600;
            break;
          }
          case '4h': {
            innerTimeframe = 'hour';
            aggregate = 4;
            timeframeSec = 14400;
            break;
          }
          case '24h': {
            innerTimeframe = 'day';
            aggregate = 1;
            timeframeSec = 86400;
            break;
          }
          case '168h': {
            innerTimeframe = 'day';
            aggregate = 7;
            timeframeSec = 604800;
            break;
          }
        }

        if (
          !network ||
          !pool_address ||
          dexPair.dex === 'pancakeswap_inf_cl:bsc' ||
          !innerTimeframe ||
          !aggregate
        ) {
          return {
            candles,
            isError,
          };
        }

        const { isSuccess, data } = await ApiCharts.getGeckoterminalChart({
          network,
          aggregate,
          before_timestamp: Math.floor(endTime / 1000),
          currency,
          limit,
          pool_address,
          timeframe: innerTimeframe,
        });

        if (isSuccess && data) {
          candles = data.data.attributes.ohlcv_list
            .filter(el => el[0] >= startTime / 1000)
            .reverse();

          let prevCandle: number[] | undefined = undefined;
          const newCandles: number[][] = [];

          for (const c of candles) {
            if (!prevCandle) {
              newCandles.push(c);
              prevCandle = c;
              continue;
            }

            if (prevCandle) {
              while (c[0] - prevCandle![0] > timeframeSec) {
                //@ts-ignore
                const newCandle = [
                  prevCandle![0] + timeframeSec,
                  prevCandle![4],
                  prevCandle![4],
                  prevCandle![4],
                  prevCandle![4],
                  0,
                ];
                newCandles.push(newCandle);
                prevCandle = newCandle;
              }

              newCandles.push(c);
              prevCandle = c;
            }
          }

          candles = newCandles as [number, number, number, number, number, number][];
        } else {
          isError = true;
        }
      } catch (error) {
        isError = true;
      }

      return { candles, isError };
    },
    [dexPair],
  );

  return load;
};

export { useLoadDexChart };
